<template>
  <v-layout row justify-center>
    <v-dialog :value="isOpen" persistent max-width="80vw" @click:outside="closeModal">
      <v-card class="pt-3">
        <v-progress-linear v-if="loading || saving" indeterminate />
        <v-form ref="form" v-model="valid" :disabled="loading" class="ma-2">
          <v-card-title class="header-text pa-0">
            {{ dialogTitle }}
          </v-card-title>
          <v-text-field
            v-model.trim="userDefinedPricingGroupName"
            :label="pricingGroupDescriptionLabel"
            :rules="rules"
            :disabled="isEditMode"
            dense
            class="mt-5"
          />
          <v-autocomplete
            v-model="selectedCDTHierarchies"
            :items="selectedCDTHierarchies"
            :label="$t('inputs.cdtHierarchies.selectedCDTs')"
            :disabled="isEditMode"
            item-text="cdtDescription"
            item-value="cdtId"
            dense
            return-object
            flat
            small-chips
            multiple
            deletable-chips
            clearable
            class="mt-2"
          />
          <v-text-field v-model="search" label="Search CDT descriptions" dense clearable />
          <div id="tree-scroll-container">
            <v-treeview
              v-model="selectedCDTHierarchies"
              :loading="loading"
              :search="search"
              selection-type="independent"
              selectable
              hoverable
              dense
              open-on-click
              return-object
              item-key="cdtId"
              item-text="cdtDescription"
              :items="CDTHierarchies"
              class="hierarchy-item mt-2"
            />
          </div>
        </v-form>

        <v-card-actions class="btn-group">
          <div v-if="!loading">
            <span :class="{ 'error--text': hasNoCDTSelected }">
              {{ $tc('inputs.cdtHierarchies.selectedCDTCount', selectedCDTHierarchies.length) }}
            </span>
            <span v-if="hasNoCDTSelected" class="error--text">
              . {{ $t('inputs.cdtHierarchies.cdtRequired') }}
            </span>
          </div>
          <v-spacer />
          <v-btn
            class="grid-btn"
            secondary
            small
            :disabled="loading || saving"
            depressed
            @click="closeModal"
          >
            {{ $t('actions.close') }}
          </v-btn>

          <!-- TODO: PRICE-4559 update method not implemented yet but can view in edit mode -->
          <v-btn
            :disabled="loading || saving || !valid || hasNoCDTSelected || isEditMode"
            small
            color="primary"
            depressed
            :loading="saving"
            @click="handleSave"
          >
            {{ $t('actions.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { get, toLower, map, omit } from 'lodash';
import axios from 'axios';
import to from 'await-to-js';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import { pricingGroupLevel, pricingGroupUndefined } from '@enums/hierarchy';
import dialogModes from '@enums/dialog-modes';

export default {
  name: 'CreatePricingGroupTree',
  props: {
    isOpen: {
      required: true,
    },
    mode: {
      type: String,
      default: () => dialogModes.create,
      validator(mode) {
        // can only be create or edit mode. delete is handled by separate modal
        return [dialogModes.create, dialogModes.edit].includes(mode);
      },
    },
    pricingGroupToEdit: {
      type: Object,
      required: false, // only used in edit mode
    },
  },
  data() {
    return {
      userDefinedPricingGroupName: '',
      search: '',
      pricingGroupLevel,
      loading: false,
      saving: false,
      selectedCDTHierarchies: [],
      CDTHierarchies: [],
      rules: [this.valueRequired, this.isValidName],
      valid: false, // starts invalid, need to add userDefinedPricingGroupName
    };
  },
  computed: {
    ...mapGetters('workpackages', ['isMasterWorkpackage']),
    ...mapGetters('hierarchy', ['getHierarchies']),
    ...mapState('workpackages', ['selectedWorkpackage']),
    dialogTitle() {
      const hierarchyLevel = this.$tc(`hierarchy.hierarchyLevels.${pricingGroupLevel}`, 1);
      // TODO: PRICE-4559 implement edit, revert view to edit
      return this.isEditMode
        ? `${this.$t('attributes.editor.titles.view')} ${hierarchyLevel}`
        : `${this.$t('attributes.editor.titles.new')} ${hierarchyLevel}`;
    },
    pricingGroupDescriptionLabel() {
      const pgLabel = this.$tc(`hierarchy.hierarchyLevels.${pricingGroupLevel}`, 1);
      return `${pgLabel} ${this.$t('inputs.cdtHierarchies.description')}`;
    },
    isEditMode() {
      return this.mode === dialogModes.edit;
    },
    cdtIdsInPricingGroupToEdit() {
      return get(this.pricingGroupToEdit, 'cdtNodes', [{ cdtId: '' }]).map(cdt => cdt.cdtId);
    },
    // form is invalid if no CDTs are selected
    hasNoCDTSelected() {
      return this.selectedCDTHierarchies.length < 1;
    },
  },
  mounted() {
    // eager validation because we have custom validation for treeview
    // no rules prop on treeview, so validation is computed
    this.$refs.form.validate();
  },
  async created() {
    this.loading = true;
    const params = {};
    if (this.isEditMode) {
      this.userDefinedPricingGroupName = this.pricingGroupToEdit.levelEntryDescription;
      params.levelEntryKey = this.pricingGroupToEdit.levelEntryKey;
    }
    const workpackageId = this.selectedWorkpackage._id;
    const [err, response] = await to(
      axios.get(`/api/hierarchy/workpackage/${workpackageId}/cdt-hierarchy`, { params })
    );
    if (err) {
      this.loading = false;
      throw new Error(err.message);
    }

    [this.CDTHierarchies, this.selectedCDTHierarchies] = response.data;
    this.loading = false;
  },
  methods: {
    ...mapActions('hierarchy', ['createHierarchyBranchFromPricingGroup']),
    ...mapMutations('notifications', ['addNotification']),
    closeModal() {
      // prevent closing while loading or saving, mainly for click:outside
      if (this.loading || this.saving) return;

      this.$emit('closePricingGroupTreeDialog');
    },
    async handleSave() {
      this.saving = true;
      const { type } = this.selectedWorkpackage;

      let jobStatus;
      let notificationPayload;
      try {
        await this.createHierarchyBranchFromPricingGroup({
          levelEntryDescription: this.userDefinedPricingGroupName,
          type,
          level: pricingGroupLevel,
          // omit keys used for tree
          cdtNodes: this.selectedCDTHierarchies.map(c =>
            omit(c, ['children', 'parent', 'disabled', 'selected'])
          ),
        });
        this.saving = false;
        jobStatus = 'finished';
        notificationPayload = this.userDefinedPricingGroupName;
        // close modal on success, keep open on error. can close with discard.
        this.closeModal();
      } catch (error) {
        console.error(error);
        jobStatus = 'failed';
      }

      this.saving = false;
      this.addNotification({
        id: `${new Date()}createHierarchyBranch`,
        jobStatus,
        baseTranslation: 'notifications.createHierarchyBranch',
        notificationPayload: notificationPayload || this.$t('errors.generic'),
      });
    },
    valueRequired(v) {
      return (
        !!v || v === 0 || this.$t('attributes.createNewChildHierarchy.childHierarchyNameRequired')
      );
    },
    isValidName(v) {
      const nameAlreadyTakenError = this.$t(
        'attributes.createNewChildHierarchy.childHierarchyNameTakenError'
      );

      // Get all names and disallowed terms, lowercased
      const existingHierarchyNames = [
        ...map(this.getHierarchies, 'levelEntryDescription'),
        pricingGroupUndefined,
      ].map(toLower);

      // when editing, allow save if name has not changed
      if (
        this.isEditMode &&
        toLower(this.pricingGroupToEdit.levelEntryDescription) === toLower(v)
      ) {
        return true;
      }

      return !existingHierarchyNames.includes(toLower(v)) || nameAlreadyTakenError;
    },
  },
};
</script>

<style lang="scss" scoped>
#tree-scroll-container {
  max-height: 50vh;
  overflow-y: auto;
}
.header-text {
  padding-top: 2rem;
  font-size: 2rem;
  color: black;
}
</style>
